/**
 * Helpers for the Content Themes & Hooks tab.
 *
 * Kept pure and free of component/framework imports so the aggregation and
 * ordering rules can be unit tested on their own — same approach as
 * lib/postSorting.js.
 *
 * Every accessor mirrors the expression its table cell renders, so the sort
 * order always reflects what the user can actually see.
 */

// Columns of the per-creator table, in render order. Each is sortable.
export const CREATOR_THEME_SORT_COLUMNS = [
  { key: 'creator', label: 'Creator', numeric: false },
  { key: 'brand', label: 'Brand', numeric: false },
  { key: 'category', label: 'Category', numeric: false },
  { key: 'tier', label: 'Tier', numeric: true },
  { key: 'posts', label: 'Posts', numeric: true },
  { key: 'topTheme', label: 'Top Theme', numeric: false },
  { key: 'topHook', label: 'Top Hook', numeric: false },
  { key: 'distinctThemes', label: 'Distinct Themes', numeric: true },
  { key: 'engagement', label: 'Avg Engagement %', numeric: true },
  { key: 'views', label: 'Total Views', numeric: true },
];

const text = (value) => String(value || '').toLowerCase();

const asArray = (value) => (Array.isArray(value) ? value : []);

/**
 * The entry with the most posts in a breakdown array. Used as a fallback when
 * the backend omits the precomputed top_theme / top_hook.
 */
const leadingEntry = (breakdown) =>
  asArray(breakdown).reduce(
    (best, item) => ((item?.post_count || 0) > (best?.post_count || 0) ? item : best),
    null,
  );

const labelOf = (explicit, breakdown) =>
  text(explicit?.label || leadingEntry(breakdown)?.label);

/**
 * Resolve the value a given column should be sorted by.
 *
 * @param {object} row   A creator row from the content-themes response.
 * @param {string} field One of the CREATOR_THEME_SORT_COLUMNS keys.
 * @returns {string|number} Lowercased string for text columns, number otherwise.
 */
export const getCreatorThemeSortValue = (row, field) => {
  const creator = row || {};

  switch (field) {
    case 'creator':
      return text(creator.name || creator.creator_id);

    case 'brand':
      return text(creator.brand);

    case 'category':
      return text(creator.category);

    // Sorted by raw follower count rather than the tier label, so the order is
    // Nano < Micro < Macro < Mega instead of alphabetical.
    case 'tier':
      return creator.followers || 0;

    case 'posts':
      return creator.post_count || 0;

    case 'topTheme':
      return labelOf(creator.top_theme, creator.theme_breakdown);

    case 'topHook':
      return labelOf(creator.top_hook, creator.hook_breakdown);

    case 'distinctThemes':
      return asArray(creator.theme_breakdown).length;

    case 'engagement':
      return creator.avg_engagement_rate || 0;

    case 'views':
      return creator.total_views || 0;

    default:
      return 0;
  }
};

/**
 * Return a new array of creator rows ordered by the given field. The input is
 * never mutated; when no field is active the original reference is returned so
 * callers can rely on referential equality to skip work.
 */
export const sortCreatorThemeRows = (rows, field, direction) => {
  if (!field) return rows;

  return [...asArray(rows)].sort((a, b) => {
    const av = getCreatorThemeSortValue(a, field);
    const bv = getCreatorThemeSortValue(b, field);

    const cmp =
      typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv));

    return direction === 'asc' ? cmp : -cmp;
  });
};

/**
 * Coerce a raw content-themes response into a predictable shape.
 *
 * This ships before the backend endpoint exists, so the tab must survive a
 * payload that is null, partial, or shaped differently than agreed — anything
 * that is not an array becomes an empty one rather than reaching a .map().
 */
export const normalizeContentThemesResponse = (raw) => {
  const data = raw || {};

  return {
    ...data,
    total_posts_analyzed: Number(data.total_posts_analyzed) || 0,
    themes: asArray(data.themes),
    hooks: asArray(data.hooks),
    creators: asArray(data.creators),
  };
};

/**
 * Whether a creator used a given theme or hook — backs the drill-down filter
 * applied to the creator table when a chip is selected.
 */
export const creatorUsedEntry = (row, entryId, kind) => {
  if (!entryId) return true;
  const creator = row || {};
  const breakdown = kind === 'hook' ? creator.hook_breakdown : creator.theme_breakdown;
  const top = kind === 'hook' ? creator.top_hook : creator.top_theme;

  if (top?.id === entryId) return true;
  return asArray(breakdown).some((item) => item?.id === entryId);
};

/**
 * Merge per-brand content-theme payloads into one brand-aware view.
 *
 * Themes and hooks with the same id are pooled across brands, keeping a
 * `by_brand` count map and the `lead_brand` that uses each one most, so the UI
 * can show who owns a theme rather than just that it exists. Creators stay one
 * row per brand — the same creator working with two brands is two data points,
 * not one merged row.
 *
 * @param {Object} byBrand  { [brandTitle]: normalized payload | null }
 */
export const mergeBrandContentThemes = (byBrand) => {
  const entries = Object.entries(byBrand || {}).filter(([, payload]) => payload);

  const mergeCollection = (key) => {
    const acc = new Map();

    entries.forEach(([brand, payload]) => {
      asArray(payload[key]).forEach((item) => {
        if (!item) return;
        const id = item.id || item.label;
        if (!id) return;

        const posts = item.post_count || 0;
        const existing = acc.get(id);

        if (!existing) {
          acc.set(id, {
            ...item,
            id,
            post_count: posts,
            creators_count: item.creators_count || 0,
            total_views: item.total_views || 0,
            total_likes: item.total_likes || 0,
            total_comments: item.total_comments || 0,
            // Engagement is weighted by post count, so a brand with 30 posts
            // moves the average more than one with 4. Averaging the averages
            // would let a tiny brand dominate.
            _engagementWeighted: (item.avg_engagement_rate || 0) * posts,
            by_brand: { [brand]: posts },
            top_creators: asArray(item.top_creators).map((c) => ({ ...c, brand })),
            example_posts: asArray(item.example_posts).map((p) => ({ ...p, brand })),
          });
          return;
        }

        existing.post_count += posts;
        existing.creators_count += item.creators_count || 0;
        existing.total_views += item.total_views || 0;
        existing.total_likes += item.total_likes || 0;
        existing.total_comments += item.total_comments || 0;
        existing._engagementWeighted += (item.avg_engagement_rate || 0) * posts;
        existing.by_brand[brand] = (existing.by_brand[brand] || 0) + posts;
        existing.top_creators = existing.top_creators
          .concat(asArray(item.top_creators).map((c) => ({ ...c, brand })))
          .sort((a, b) => (b.post_count || 0) - (a.post_count || 0));
        existing.example_posts = existing.example_posts.concat(
          asArray(item.example_posts).map((p) => ({ ...p, brand })),
        );
      });
    });

    return Array.from(acc.values())
      .map(({ _engagementWeighted, ...item }) => ({
        ...item,
        avg_engagement_rate: item.post_count > 0 ? _engagementWeighted / item.post_count : 0,
        lead_brand: Object.entries(item.by_brand).reduce(
          (best, [brand, count]) => (count > (best[1] || 0) ? [brand, count] : best),
          [null, 0],
        )[0],
      }))
      .sort((a, b) => (b.post_count || 0) - (a.post_count || 0));
  };

  const creators = entries.flatMap(([brand, payload]) =>
    asArray(payload.creators).map((c) => ({ ...c, brand })),
  );

  /**
   * Reconcile each entry against the creator rows the table actually renders.
   *
   * creators_count must not be the sum of the per-brand figures: a creator
   * working with two brands would be counted twice, so a row could claim more
   * creators than the table beneath it lists. Where we have creator rows we
   * count them; where the backend returned none (or paginated them away) we
   * fall back to its own figure, which is the best available.
   */
  const reconcile = (list, kind) => {
    const totalPosts = list.reduce((sum, item) => sum + (item.post_count || 0), 0);

    return list.map((item) => ({
      ...item,
      creators_count: creators.length
        ? creators.filter((c) => creatorUsedEntry(c, item.id, kind)).length
        : item.creators_count,
      // Recomputed against the merged total; the per-brand share carried in on
      // the spread describes a set that no longer exists.
      share_of_posts: totalPosts > 0 ? (item.post_count / totalPosts) * 100 : 0,
    }));
  };

  return {
    themes: reconcile(mergeCollection('themes'), 'theme'),
    hooks: reconcile(mergeCollection('hooks'), 'hook'),
    creators,
    total_posts_analyzed: entries.reduce(
      (sum, [, payload]) => sum + (Number(payload.total_posts_analyzed) || 0),
      0,
    ),
    // Per-brand totals, so "how many posts did it analyse for Pampers?" is
    // answerable from the screen rather than by querying the API.
    posts_by_brand: Object.fromEntries(
      entries.map(([brand, payload]) => [brand, Number(payload.total_posts_analyzed) || 0]),
    ),
  };
};
